/**
 * Type guards and helpers for rich chat card rendering.
 * Each guard validates the output shape from a specific agent tool.
 */

import type { AuditId, DocumentId, ChecklistItemId } from "@/packages/types";

// Re-export status configs used by card components
export {
  AUDIT_STATUS_CONFIG,
  formatDeadline,
} from "@/components/audits/constants";
export { CHECKLIST_STATUS_CONFIG } from "@/components/audits/tabs/checklist/ChecklistRow";

// Re-export unified ActivityEvent interface from ActivityFeed
// This ensures consistency across all activity display components
export type { ActivityEvent } from "@/components/ui/ActivityFeed/types";
export {
  getInitials,
  getAvatarColor,
} from "@/hooks/activityHelpers";

// ---------------------------------------------------------------------------
// Type definitions matching agent tool output shapes
// ---------------------------------------------------------------------------

export interface AuditListItem {
  id: string;
  displayName: string;
  code: string;
  status: string;
  deadline: number;
  createdAt: number;
  templateName: string;
  completionPercent: number;
}

export interface AuditDetailResult {
  id: string;
  displayName: string;
  code: string;
  status: string;
  description: string | null;
  deadline: number;
  templateName: string;
  companyName: string;
  notes: string | null;
  totalChecklistItems: number;
  completedItems: number;
  missingItems: number;
  uploadedItems: number;
  completionPercent: number;
  categoryCount: number;
  totalDocuments: number;
  linkedDocuments: number;
  aiSummaryExcerpt: string | null;
  tags: string[];
}

export interface ChecklistCategory {
  id: string;
  name: string;
  order: number;
  items: ChecklistItem[];
  completedCount: number;
  totalCount: number;
}

export interface ChecklistItem {
  id: string;
  description: string;
  status: string;
  required: boolean;
  linkedDocCount: number;
  hasAiSummary: boolean;
  clauseRefs: string[];
}

export interface ChecklistResult {
  auditId: AuditId;
  categories: ChecklistCategory[];
  totalItems: number;
  completedItems: number;
}

export interface ChecklistItemDetailResult {
  id: string;
  description: string;
  status: string;
  required: boolean;
  categoryName: string;
  auditId: AuditId;
  auditName: string;
  notes: string | null;
  clauseRefs: string[];
  summaryReport: string | null;
  informationReport: string | null;
  linkedDocuments: { documentId: DocumentId; fileName: string }[];
  suggestedDocuments: {
    documentId: DocumentId;
    fileName: string;
    confidence: string;
    reason: string | null;
  }[];
  completedAt: number | null;
}

export interface ClauseSection {
  title: string;
  clauses: { ref: string; title: string; description?: string }[];
}

export interface ClauseSearchResult {
  standardName: string;
  standardCode: string;
  sections: ClauseSection[];
  totalClauses: number;
}

export interface CoverageLink {
  documentId: DocumentId;
  documentName: string;
  checklistItemId: ChecklistItemId;
  itemDescription: string;
  categoryName: string;
}

export interface DocChecklistLinksResult {
  auditId: AuditId;
  links: CoverageLink[];
  totalLinks: number;
  itemsWithDocuments: number;
  itemsWithoutDocuments: number;
  totalChecklistItems: number;
}

// Raw activity event from backend (before transformation to unified format)
export interface RawActivityEvent {
  id: string;
  eventType: string;
  description: string;
  entityType: string;
  entityId: string;
  timestamp: number;
  userName: string | null;
}

/**
 * Transform raw backend activity events to the unified ActivityEvent format.
 * This is used by the chat component to display activities in a consistent way.
 */
import type { ActivityEvent as UnifiedActivityEvent } from "@/components/ui/ActivityFeed/types";
import { getInitials, getAvatarColor } from "@/hooks/activityHelpers";

export function transformToActivityEvent(
  raw: RawActivityEvent,
): UnifiedActivityEvent {
  const actorName = raw.userName ?? "System";

  return {
    id: raw.id,
    type: raw.eventType,
    category: inferActivityCategory(raw.eventType),
    title: raw.description,
    description: raw.description,
    actor: {
      name: actorName,
      initials: getInitials(actorName),
      avatarColor: getAvatarColor(actorName),
    },
    timestamp: new Date(raw.timestamp),
    relatedEntity: {
      name: raw.entityType,
      type: raw.entityType,
    },
  };
}

/**
 * Infer the activity category from the event type.
 */
function inferActivityCategory(
  eventType: string,
): "status" | "content" | "people" | "system" {
  if (eventType.startsWith("audit")) return "status";
  if (eventType.startsWith("document")) return "content";
  if (eventType.startsWith("checklist")) return "content";
  if (eventType.includes("ai") || eventType.includes("summary"))
    return "system";
  return "system";
}

export interface SuggestedDocsResult {
  checklistItemId: string;
  description: string;
  suggestedDocuments: {
    documentId: string;
    fileName: string;
    confidence: string;
    reason: string | null;
  }[];
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

function isObject(val: unknown): val is Record<string, unknown> {
  return typeof val === "object" && val !== null && !Array.isArray(val);
}

export function isAuditListResult(output: unknown): output is AuditListItem[] {
  return (
    Array.isArray(output) &&
    output.length > 0 &&
    isObject(output[0]) &&
    "id" in output[0] &&
    "displayName" in output[0] &&
    "status" in output[0] &&
    "completionPercent" in output[0]
  );
}

export function isAuditDetailResult(
  output: unknown,
): output is AuditDetailResult {
  return (
    isObject(output) &&
    "id" in output &&
    "displayName" in output &&
    "totalChecklistItems" in output &&
    "completionPercent" in output
  );
}

export function isChecklistResult(output: unknown): output is ChecklistResult {
  return (
    isObject(output) &&
    "auditId" in output &&
    "categories" in output &&
    "totalItems" in output
  );
}

export function isChecklistItemDetailResult(
  output: unknown,
): output is ChecklistItemDetailResult {
  return (
    isObject(output) &&
    "id" in output &&
    "description" in output &&
    "status" in output &&
    "auditId" in output &&
    "categoryName" in output
  );
}

export function isClauseSearchResult(
  output: unknown,
): output is ClauseSearchResult {
  return (
    isObject(output) &&
    "standardName" in output &&
    "sections" in output &&
    "totalClauses" in output
  );
}

export function isDocChecklistLinksResult(
  output: unknown,
): output is DocChecklistLinksResult {
  return (
    isObject(output) &&
    "auditId" in output &&
    "links" in output &&
    "totalLinks" in output
  );
}

export function isActivityResult(
  output: unknown,
): output is UnifiedActivityEvent[] {
  return (
    Array.isArray(output) &&
    output.length > 0 &&
    isObject(output[0]) &&
    "id" in output[0] &&
    "eventType" in output[0] &&
    "timestamp" in output[0]
  );
}

export function isSuggestedDocsResult(
  output: unknown,
): output is SuggestedDocsResult {
  return (
    isObject(output) &&
    "checklistItemId" in output &&
    "suggestedDocuments" in output
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get a color class for completion percentage */
export function completionColor(percent: number): string {
  if (percent >= 80) return "text-emerald-500";
  if (percent >= 50) return "text-amber-500";
  return "text-red-400";
}

/** Get a bar color for completion percentage */
export function completionBarColor(percent: number): string {
  if (percent >= 80) return "bg-emerald-500";
  if (percent >= 50) return "bg-amber-500";
  return "bg-red-400";
}

/** Format relative time from a timestamp */
export function relativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/** Get a dot color for activity event types */
export function activityDotColor(eventType: string): string {
  if (eventType.startsWith("audit")) return "bg-orange-400";
  if (eventType.startsWith("document")) return "bg-blue-400";
  if (eventType.startsWith("checklist")) return "bg-emerald-400";
  return "bg-muted";
}
